import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';

interface RawJsonViewerProps {
  data: any;
  title?: string;
  titleSize?: 'sm' | 'lg';
  className?: string;
}

const RawJsonViewer = ({ data, title = 'Raw Status Payload', titleSize = 'lg', className }: RawJsonViewerProps) => {
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const preRef = useRef<HTMLPreElement>(null);
  const matchRefs = useRef<(HTMLElement | null)[]>([]);

  const jsonString = useMemo(() => JSON.stringify(data, null, 2), [data]);

  const { parts, matchCount } = useMemo(() => {
    if (!search || search.length < 2) {
      return { parts: null, matchCount: 0 };
    }
    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const split = jsonString.split(regex);
    const count = split.filter((_, i) => i % 2 === 1).length;
    return { parts: split, matchCount: count };
  }, [jsonString, search]);

  // Reset active index when search changes
  useEffect(() => {
    setActiveIndex(0);
    matchRefs.current = [];
  }, [search]);

  // Scroll active match into view
  useEffect(() => {
    if (matchCount > 0 && matchRefs.current[activeIndex]) {
      matchRefs.current[activeIndex]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [activeIndex, matchCount]);

  // Auto-scroll to first match when results appear
  useEffect(() => {
    if (matchCount > 0) {
      // Small delay to let refs populate after render
      const t = setTimeout(() => {
        matchRefs.current[0]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [matchCount]);

  const goNext = useCallback(() => {
    if (matchCount > 0) setActiveIndex(i => (i + 1) % matchCount);
  }, [matchCount]);

  const goPrev = useCallback(() => {
    if (matchCount > 0) setActiveIndex(i => (i - 1 + matchCount) % matchCount);
  }, [matchCount]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) goPrev(); else goNext();
    }
  }, [goNext, goPrev]);

  const titleClass = titleSize === 'sm'
    ? 'text-sm font-semibold text-gray-900'
    : 'text-lg font-semibold text-gray-900';

  // Track which match index we're rendering
  let matchIdx = -1;

  return (
    <div className={className ?? 'card'}>
      <div className="flex items-center justify-between mb-4">
        <h4 className={titleClass}>{title}</h4>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              className="pl-8 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-56"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {search.length >= 2 && (
            <>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {matchCount > 0 ? `${activeIndex + 1} / ${matchCount}` : '0 matches'}
              </span>
              {matchCount > 1 && (
                <div className="flex items-center">
                  <button
                    onClick={goPrev}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title="Previous match (Shift+Enter)"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={goNext}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title="Next match (Enter)"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <pre ref={preRef} className="bg-gray-50 p-4 rounded-lg overflow-auto text-xs font-mono text-gray-800 max-h-96">
        {parts ? (
          parts.map((part, i) => {
            if (i % 2 === 1) {
              matchIdx++;
              const idx = matchIdx;
              const isActive = idx === activeIndex;
              return (
                <mark
                  key={i}
                  ref={(el) => { matchRefs.current[idx] = el; }}
                  className={isActive
                    ? 'bg-orange-400 text-white rounded-sm ring-2 ring-orange-500'
                    : 'bg-yellow-300 text-gray-900 rounded-sm'
                  }
                >
                  {part}
                </mark>
              );
            }
            return <span key={i}>{part}</span>;
          })
        ) : (
          jsonString
        )}
      </pre>
    </div>
  );
};

export default RawJsonViewer;
